import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { codeVisit, setCodeVisit } from './codeVisit';
import './RemoteController.css';
import { createClient } from '@supabase/supabase-js';
import { FRAME_MS, TWIRL_PATTERNS, VIBE_PATTERNS, type PagePattern } from './patterns';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const APP_STORE = 'https://apps.apple.com/app/id6446037119';
const PLAY_STORE = 'https://play.google.com/store/apps/details?id=com.mbtechnologies.Fluffer';

// Device types that determine which controls to show
type DeviceType = 'MBCtrl' | 'MBBunny' | 'Toys' | 'default';

const asDeviceType = (v: unknown): DeviceType => (v === 'MBCtrl' || v === 'MBBunny' || v === 'Toys' ? v : 'default');

/** Where a shared link's room and key are kept once read: this tab's history entry (survives a reload, never sent anywhere, not in the address bar). */
const LINK_STATE = 'flufferLink';
/** Set on this tab's history entry (in place of LINK_STATE) once the sharer ended the session. */
const ENDED_STATE = 'flufferEnded';
/** Rooms whose session ended, for this tab (sessionStorage): opening one again shows it ended. */
const ENDED_ROOMS = 'flufferEndedRooms';
/** After the last change to a control, its level is sent once more (catches a burst delivered out of order). */
const SETTLE_MS = 250;

function endedRooms(): string[] {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(ENDED_ROOMS) || '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * The sharer ended the session: forget the room's key everywhere this tab
 * kept it (the code visit in memory, the history entry), so Back, Forward or
 * a reload lands on "Session ended" instead of quietly rejoining a dead room.
 */
function markEnded(roomId: string) {
  setCodeVisit(null);
  try {
    window.sessionStorage.setItem(ENDED_ROOMS, JSON.stringify([...endedRooms().filter(r => r !== roomId), roomId].slice(-20)));
  } catch {
    /* the history entry below still covers Back and reload */
  }
  try {
    const state: Record<string, unknown> = { ...((window.history.state ?? {}) as Record<string, unknown>), [ENDED_STATE]: true };
    delete state[LINK_STATE];
    window.history.replaceState(state, '', window.location.pathname);
  } catch {
    /* nothing kept */
  }
}

interface PageLink {
  roomId: string;
  messageKey: string;
  deviceType: DeviceType;
  /** Code mode's waiting room; the room's real key only comes with 'admit'. */
  lobbyKey: string;
  /** Came in by typing a code: knock in the lobby and wait to be let in. */
  viaCode: boolean;
  /** The session this page was for already ended (see markEnded): nothing to join. */
  ended?: boolean;
  /** The address had part of a link (a room or a key, not both). */
  partial?: boolean;
}

/**
 * What this page is for. A shared link carries RoomId/MessageKey in the
 * fragment (#…, never sent to the server); older apps put them in the query
 * (?…), which still works. Either way they're moved out of the address bar
 * (history.replaceState) into this tab's history entry, so they don't sit in
 * the URL, history sync or a copy-paste, and a reload still works. A code
 * visit comes from /remote in memory only (codeVisit).
 */
function parseLink(): PageLink {
  const link = readLink();
  const state = (window.history.state ?? {}) as Record<string, unknown>;
  if ((link.roomId && endedRooms().includes(link.roomId)) || (!link.roomId && state[ENDED_STATE])) {
    return { roomId: '', messageKey: '', deviceType: 'default', lobbyKey: '', viaCode: false, ended: true };
  }
  return link;
}

function readLink(): PageLink {
  const visit = codeVisit();
  if (visit) {
    return { roomId: visit.roomId, messageKey: '', lobbyKey: visit.lobbyKey, deviceType: asDeviceType(visit.deviceType), viaCode: true };
  }
  const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const fromQuery = new URLSearchParams(window.location.search);
  const p = fromHash.get('RoomId') || fromHash.get('roomId') ? fromHash : fromQuery;
  const roomId = p.get('RoomId') || p.get('roomId') || '';
  const messageKey = p.get('MessageKey') || p.get('messageKey') || '';
  const state = (window.history.state ?? {}) as Record<string, unknown>;
  if (roomId && messageKey) {
    const link = { roomId, messageKey, deviceType: p.get('DeviceType') || p.get('deviceType') || '' };
    try {
      window.history.replaceState({ ...state, [LINK_STATE]: link }, '', window.location.pathname);
    } catch {
      /* the address bar keeps it; still works */
    }
    return { roomId, messageKey, deviceType: asDeviceType(link.deviceType), lobbyKey: '', viaCode: false };
  }
  // Reloaded after the link was read: it's in this tab's history entry.
  const kept = state[LINK_STATE] as { roomId?: unknown; messageKey?: unknown; deviceType?: unknown } | undefined;
  if (kept && typeof kept.roomId === 'string' && typeof kept.messageKey === 'string') {
    return { roomId: kept.roomId, messageKey: kept.messageKey, deviceType: asDeviceType(kept.deviceType), lobbyKey: '', viaCode: false };
  }
  // Half a link (cut off when it was pasted): say so, rather than ask for a code.
  return { roomId: '', messageKey: '', deviceType: 'default', lobbyKey: '', viaCode: false, partial: !!(roomId || messageKey) };
}

/** One slider on the page: a joystick on the sharer's Controller. */
interface Control {
  id: string;
  /** Turns both ways around a centre stop (twirl/thrust). */
  bidi: boolean;
  /** From newer apps: a thrust motor, up and down (Buck) or in and out (Jack). */
  kind?: 'thrust' | 'thrust-side';
}

/** Thrust: a double arrow, as the app draws it. */
function ThrustIcon({ sideways }: { sideways: boolean }) {
  return (
    <svg className="thrust-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={sideways ? { transform: 'rotate(90deg)' } : undefined} aria-hidden="true">
      <path d="M12 3v18" />
      <path d="m8 7 4-4 4 4" />
      <path d="m8 17 4 4 4-4" />
    </svg>
  );
}

/** Before the app says what the toy has (older apps never do), guess from the link. */
const controlsFor = (deviceType: DeviceType): Control[] =>
  deviceType === 'MBCtrl' || deviceType === 'MBBunny'
    ? [
        { id: 'joystick-1', bidi: false },
        { id: 'joystick-2', bidi: true },
      ]
    : [{ id: 'joystick-1', bidi: false }];
/**
 * Chains between neighbouring vibration sliders tie them together. Never on
 * Motorbunny or Buck (their second motor is the twirl or the buck, even when
 * the app sends it as one-way), never on a twirl/thrust slider.
 */
const canLink = (deviceType: DeviceType, controls: Control[]) =>
  deviceType !== 'MBCtrl' && deviceType !== 'MBBunny' && controls.filter(c => !c.bidi).length >= 2;
const linkKey = (a: Control, b: Control) => `${a.id}>${b.id}`;
/** The sliders that move with `c` (itself included): its run of linked neighbours. */
const linkedRun = (controls: Control[], links: Record<string, boolean>, c: Control): Control[] => {
  const i = controls.indexOf(c);
  if (i < 0 || c.bidi) return [c];
  const tied = (j: number) => !controls[j].bidi && !controls[j + 1].bidi && !!links[linkKey(controls[j], controls[j + 1])];
  let start = i;
  while (start > 0 && tied(start - 1)) start--;
  let end = i;
  while (end < controls.length - 1 && tied(end)) end++;
  return controls.slice(start, end + 1);
};
// A two-way control is always a twirl: thrust (Buck, Jack) is one-way and comes
// with its kind in the layout. In a group of a twirling and a thrusting toy, the
// twirl's strength drives the thrust too (the app does that per toy).
const turnLabel = (_deviceType: DeviceType) => 'Twirl';
const restOf = (c: Control) => (c.bidi ? 0.5 : 0);

const topicFor = (roomId: string, key: string) => `room:${roomId}:${key}`;
/** The room key a sharer's 'admit' hands over. */
const ROOM_KEY = /^[A-Za-z0-9]{16}$/;

// ── Code mode: the room key, encrypted to the visitor who knocked ────
// Protocol (the app does the same): the visitor knocks with a random id and
// an ephemeral ECDH P-256 public key; 'admit' carries the app's public key,
// an AES-GCM iv and ciphertext. Key = HKDF-SHA256(ECDH X, salt 32 zero
// bytes, info "fluffer-remote-admit-v1"); additional data = the visitor id.

const ID_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const ADMIT_INFO = 'fluffer-remote-admit-v1';
const utf8 = (text: string) => new TextEncoder().encode(text);

function toB64url(bytes: Uint8Array) {
  let bin = '';
  bytes.forEach(b => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromB64url(text: string) {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(bin, c => c.charCodeAt(0));
}

/** Who this page is in the lobby, for this visit only (memory, never stored). */
interface Visitor {
  id: string;
  pub: string;
  priv: CryptoKey;
}

/** 16 random letters/digits (62 chars; 248 = 62 × 4, so values ≥ 248 are dropped to keep it unbiased). */
function randomId(): string {
  let id = '';
  while (id.length < 16) {
    for (const b of crypto.getRandomValues(new Uint8Array(32))) {
      if (b < 248 && id.length < 16) id += ID_CHARS[b % 62];
    }
  }
  return id;
}

async function makeVisitor(): Promise<Visitor> {
  const pair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']);
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey));
  return { id: randomId(), pub: toB64url(raw), priv: pair.privateKey };
}

/** The room key from an 'admit' meant for this visitor, or null if it doesn't open. */
async function openAdmit(me: Visitor, payload: { pub?: unknown; iv?: unknown; ct?: unknown }): Promise<string | null> {
  try {
    if (typeof payload.pub !== 'string' || typeof payload.iv !== 'string' || typeof payload.ct !== 'string') return null;
    const iv = fromB64url(payload.iv);
    if (iv.length !== 12) return null;
    const appKey = await crypto.subtle.importKey('raw', fromB64url(payload.pub), { name: 'ECDH', namedCurve: 'P-256' }, false, []);
    const shared = await crypto.subtle.deriveBits({ name: 'ECDH', public: appKey }, me.priv, 256);
    const hkdf = await crypto.subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
    const aes = await crypto.subtle.deriveKey(
      { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: utf8(ADMIT_INFO) },
      hkdf,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt'],
    );
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv, additionalData: utf8(me.id) }, aes, fromB64url(payload.ct));
    const key = new TextDecoder().decode(plain);
    return ROOM_KEY.test(key) ? key : null;
  } catch {
    return null;
  }
}

type Mode = 'control' | 'draw' | 'patterns';
type Lane = 'vibe' | 'turn';

/** A drawing is played back as this many steps, left to right, looping. */
const DRAW_STEPS = 50;

const isAndroid = /android/i.test(navigator.userAgent);

/** Keep getting a finger's moves outside the element (some browsers refuse; that's fine). */
function capture(el: Element, pointerId: number) {
  try {
    el.setPointerCapture(pointerId);
  } catch {
    /* not capturable */
  }
}
function release(el: Element, pointerId: number) {
  try {
    el.releasePointerCapture(pointerId);
  } catch {
    /* not captured */
  }
}

// ── Small drawings, in the app's style ───────────────────────────────

/** The Bunny: the Fluffer piece people drag (same drawing as the app's). */
export function Bunny({ size = 44, glow = 0.5 }: { size?: number; glow?: number }) {
  const g = Math.max(0, Math.min(1, glow));
  return (
    <svg width={size} height={size} viewBox="-20 -20 140 140" aria-hidden="true">
      <defs>
        <radialGradient id="bunny-aura" cx="50" cy="55" r="70" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FF2FBF" stopOpacity={0.25 + g * 0.55} />
          <stop offset="1" stopColor="#FF2FBF" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="bunny-fur" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#FFD3F1" />
        </linearGradient>
      </defs>
      <circle cx="50" cy="55" r="70" fill="url(#bunny-aura)" />
      <ellipse cx="36" cy="28" rx="10" ry="24" transform="rotate(-14 36 28)" fill="url(#bunny-fur)" />
      <ellipse cx="64" cy="28" rx="10" ry="24" transform="rotate(14 64 28)" fill="url(#bunny-fur)" />
      <ellipse cx="36" cy="29" rx="4.5" ry="15" transform="rotate(-14 36 29)" fill="#2A1240" opacity="0.35" />
      <ellipse cx="64" cy="29" rx="4.5" ry="15" transform="rotate(14 64 29)" fill="#2A1240" opacity="0.35" />
      <circle cx="50" cy="64" r="30" fill="url(#bunny-fur)" />
      <path d="M50 80 C 36 70, 34 58, 42 55 C 46 53.6, 49 55.5, 50 58 C 51 55.5, 54 53.6, 58 55 C 66 58, 64 70, 50 80 Z" fill="#2A1240" />
    </svg>
  );
}

const ICONS: Record<string, string> = {
  sliders: 'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
  pencil: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z',
  waves: 'M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1',
  play: 'M6 3l14 9-14 9z',
  square: 'M5 5h14v14H5z',
  eraser: 'M7 21l-4.3-4.3a1 1 0 0 1 0-1.4l10-10a1 1 0 0 1 1.4 0l5.6 5.6a1 1 0 0 1 0 1.4L11 21M22 21H7M5 11l9 9',
  heart: 'M19 14c1.5-1.5 3-3.2 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.8 0-3 .5-4.5 2-1.5-1.5-2.7-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4 3 5.5l7 7z',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  gamepad: 'M6 12h4M8 10v4M15 13h.01M18 11h.01M17.3 5H6.7a4 4 0 0 0-4 3.6L2 15a3 3 0 0 0 5.4 2l1.2-1.6A2 2 0 0 1 10.2 15h3.6a2 2 0 0 1 1.6.8l1.2 1.6A3 3 0 0 0 22 15l-.7-6.4A4 4 0 0 0 17.3 5',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  music: 'M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0M21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0',
  rotate: 'M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5',
  // Lucide link-2 / link-2-off, as on the app's Controller.
  link: 'M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 1 1 0 10h-2M8 12h8',
  unlink: 'M9 17H7A5 5 0 0 1 7 7M15 7h2a5 5 0 0 1 4 8M8 12h4M2 2l20 20',
  power: 'M12 2v10M18.4 6.6a9 9 0 1 1-12.8 0',
};

function Icon({ name, size = 18 }: { name: keyof typeof ICONS; size?: number }) {
  const filled = name === 'play' || name === 'square';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={filled ? 0 : 2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[name]} />
    </svg>
  );
}

/** A pattern's shape, like the app's pattern covers. */
function Sparkline({ values, bidi }: { values: number[]; bidi?: boolean }) {
  const w = 100;
  const h = 40;
  // Show at least ~3 s so short loops read as a rhythm, not one blip.
  const shown = values.length >= 30 ? values : Array.from({ length: Math.ceil(30 / values.length) * values.length }, (_, i) => values[i % values.length]);
  const pts = shown.map((v, i) => [(i / Math.max(1, shown.length - 1)) * w, h - 4 - v * (h - 8)]);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const base = bidi ? h / 2 : h;
  const color = bidi ? '#B57BFF' : '#FF5FCF';
  const id = bidi ? 'spark-fill-turn' : 'spark-fill';
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="spark" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.55" />
          <stop offset="1" stopColor={color} stopOpacity={bidi ? 0.55 : 0} />
        </linearGradient>
      </defs>
      {bidi && <line x1="0" x2={w} y1={base} y2={base} stroke="rgba(255,255,255,0.35)" strokeWidth="1" vectorEffect="non-scaling-stroke" />}
      <path d={`${line} L${w} ${base} L0 ${base} Z`} fill={`url(#${id})`} opacity={bidi ? 0.5 : 1} />
      <path d={line} fill="none" stroke={color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

function StoreButtons() {
  const apple = (
    <a key="ios" className="store-btn" href={APP_STORE} target="_blank" rel="noopener noreferrer">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M16.4 12.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.8-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.8 1.2 1.8 2.6 3.1 2.5 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.5-1-2.5-4.2zM14 5c.7-.8 1.1-1.9 1-3-1 0-2.1.6-2.8 1.4-.6.7-1.2 1.8-1 2.9 1 .1 2.1-.5 2.8-1.3z" />
      </svg>
      <span>
        <small>Download on the</small>App Store
      </span>
    </a>
  );
  const google = (
    <a key="android" className="store-btn" href={PLAY_STORE} target="_blank" rel="noopener noreferrer">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M3.6 2.3c-.2.2-.3.6-.3 1v17.4c0 .4.1.8.3 1l9.7-9.7zM14.4 13.1l2.6 2.6-11.2 6.4c-.4.2-.8.2-1.1.1zM18.4 9.8l2.6 1.5c.8.5.8 1.3 0 1.8l-2.7 1.5-2.8-2.8zM4.7 1.8c.3-.1.7-.1 1.1.1L17 8.3l-2.6 2.6z" />
      </svg>
      <span>
        <small>Get it on</small>Google Play
      </span>
    </a>
  );
  return <div className="stores">{isAndroid ? [google, apple] : [apple, google]}</div>;
}

const PERKS: { icon: keyof typeof ICONS; text: string }[] = [
  { icon: 'heart', text: 'Play together: drive each other’s toys at the same time' },
  { icon: 'sliders', text: 'Full controls, patterns and vibetracks you can send' },
  { icon: 'users', text: 'Vibe Board: one board for the whole group' },
  { icon: 'gamepad', text: 'Games and audio that play their toy' },
  { icon: 'lock', text: 'Chat that’s end-to-end encrypted' },
];

export function GetFluffer({ title = 'Want more? Get Fluffer' }: { title?: string }) {
  return (
    <section className="promo">
      <div className="promo-head">
        <img src="/fluffer-app-icon.png" alt="" className="promo-icon" />
        <div>
          <h3>{title}</h3>
          <p>Add the person who shared this as a friend in Fluffer and you unlock a lot more.</p>
        </div>
      </div>
      <ul className="perks">
        {PERKS.map(p => (
          <li key={p.text}>
            <span className="perk-icon">
              <Icon name={p.icon} size={16} />
            </span>
            {p.text}
          </li>
        ))}
      </ul>
      <StoreButtons />
    </section>
  );
}

// ── The page ────────────────────────────────────────────────────────

/** A drawing lane, in 0–1 on both axes (x left→right, y 0 = top). */
type Stroke = { x: number; y: number }[];

/** A drawing as levels left to right (the latest stroke over each x wins). */
function strokeLevels(points: Stroke): number[] {
  if (points.length < 2) return [];
  const out: (number | null)[] = new Array(DRAW_STEPS).fill(null);
  for (const p of points) out[Math.min(DRAW_STEPS - 1, Math.floor(p.x * DRAW_STEPS))] = 1 - p.y;
  const first = out.findIndex(v => v !== null);
  const last = DRAW_STEPS - 1 - [...out].reverse().findIndex(v => v !== null);
  const filled: number[] = [];
  let prev = out[first] as number;
  for (let i = first; i <= last; i++) {
    if (out[i] !== null) prev = out[i] as number;
    filled.push(prev);
  }
  return filled;
}

const RemoteController: React.FC = () => {
  const { roomId, messageKey, lobbyKey, deviceType, viaCode, ended: endedBefore, partial } = useMemo(parseLink, []);
  /** This page's id on the room ('join'/'leave'), so the sharer can count who's on it and tell someone new from a page coming back. Memory only. */
  const vid = useMemo(randomId, []);
  /** Code mode, reloaded: the visit (and its code) lived in memory only. */
  const location = useLocation();
  const lostCodeVisit = !roomId && !endedBefore && (location.state as { via?: string } | null)?.via === 'code';
  /** The room's key: from the link, or (code mode) from the sharer's 'admit', kept in memory only. */
  const [mainKey, setMainKey] = useState<string | null>(viaCode ? null : messageKey);
  const topic = useMemo(() => (roomId && mainKey ? topicFor(roomId, mainKey) : null), [roomId, mainKey]);
  const validLink = !!roomId && (viaCode ? !!lobbyKey : !!messageKey);
  const [connected, setConnected] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  useEffect(() => {
    if (sessionEnded && roomId) markEnded(roomId);
  }, [sessionEnded, roomId]);
  /** Their phone put Fluffer to sleep (iOS, after a while in the background). */
  const [away, setAway] = useState(false);
  /** Code mode: 'knocking' until the sharer lets them in ('open') or doesn't ('denied'). */
  const [admission, setAdmission] = useState<'open' | 'knocking' | 'denied'>(viaCode ? 'knocking' : 'open');
  /** Nothing reaches the toy before the sharer lets this page in. */
  const admittedRef = useRef(!viaCode);
  /** Code mode: this visit's lobby id and ECDH keys (memory only). */
  const visitorRef = useRef<Promise<Visitor> | null>(null);
  // Still waiting after 30 s: offer a way out (a new code) instead of an endless wait.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (admission !== 'knocking') return setSlow(false);
    const t = window.setTimeout(() => setSlow(true), 30_000);
    return () => window.clearTimeout(t);
  }, [admission]);
  const [controls, setControls] = useState<Control[]>(() => controlsFor(deviceType));
  const vibeControls = controls.filter(c => !c.bidi);
  const turnControls = controls.filter(c => c.bidi);
  const hasTurn = turnControls.length > 0;
  const turnName = turnLabel(deviceType);
  /** Each control's level, as last sent (or as the sharer moved it). */
  const [levels, setLevels] = useState<Record<string, number>>({});
  const levelOf = (c: Control) => levels[c.id] ?? restOf(c);
  /** Motors switched off with their power button: they get their rest level, whatever the slider says. */
  const [off, setOff] = useState<Record<string, boolean>>({});
  const offRef = useRef(off);
  // The live meter shows what the toy gets: a motor that's off counts as resting.
  const outOf = (c: Control) => (off[c.id] ? restOf(c) : levelOf(c));
  const vibeLevel = Math.max(0, ...vibeControls.map(outOf));
  const turnLevel = hasTurn ? outOf(turnControls[0]) : 0.5;
  const [mode, setMode] = useState<Mode>('control');
  const [lane, setLaneState] = useState<Lane>('vibe');
  const [strokes, setStrokes] = useState<Record<Lane, Stroke>>({ vibe: [], turn: [] });
  const [isDrawing, setIsDrawing] = useState(false);
  /** What's playing on each lane: a pattern id, 'draw', or nothing. */
  const [playing, setPlaying] = useState<Record<Lane, string | null>>({ vibe: null, turn: null });
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const waveCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const wavePointsRef = useRef<number[]>(new Array(60).fill(0));
  const vibeLevelRef = useRef(0);
  vibeLevelRef.current = vibeLevel;
  const controlsRef = useRef(controls);
  controlsRef.current = controls;
  /** Chained neighbours (by linkKey); none to start with. */
  const [links, setLinks] = useState<Record<string, boolean>>({});
  const linkable = canLink(deviceType, controls);
  const linksRef = useRef(links);
  linksRef.current = linkable ? links : {};
  /** The slider touched last: linking snaps the others to it. */
  const lastTouchedRef = useRef<string | null>(null);
  /** Per lane: the levels on repeat and where we are in them. */
  const loops = useRef<Record<Lane, { values: number[]; i: number } | null>>({ vibe: null, turn: null });
  const ticker = useRef<number | null>(null);

  // Realtime can deliver a burst of messages out of order, so the toy could
  // end on an older level than the slider shows. Each send carries this
  // page's id and a rising number (the app drops anything older than what it
  // has from this page for that control), and the last level is sent once
  // more after the slider goes quiet.
  const seqRef = useRef(0);
  const settleRef = useRef<Map<string, number>>(new Map());
  // `on` and `level` carry the power button: the app shows it on its own
  // power button and keeps its slider at `level`; older apps just play
  // `amplitude` (the rest level while off).
  const send = useCallback(
    (joystickId: string, amplitude: number, level: number, on: boolean) => {
      channelRef.current?.send({ type: 'broadcast', event: 'control', payload: { joystickId, amplitude, level, on, vid, seq: ++seqRef.current } });
    },
    [vid],
  );
  const publish = useCallback(
    (joystickId: string, level: number) => {
      if (!admittedRef.current) return;
      const control = controlsRef.current.find(c => c.id === joystickId);
      const on = !(offRef.current[joystickId] && control);
      const amplitude = on || !control ? level : restOf(control);
      send(joystickId, amplitude, level, on);
      const settle = settleRef.current;
      window.clearTimeout(settle.get(joystickId));
      settle.set(
        joystickId,
        window.setTimeout(() => {
          settle.delete(joystickId);
          if (admittedRef.current) send(joystickId, amplitude, level, on);
        }, SETTLE_MS),
      );
    },
    [send],
  );
  useEffect(() => {
    const settle = settleRef.current;
    return () => settle.forEach(t => window.clearTimeout(t));
  }, []);

  /** Set one lane (all its controls) to a level, here and on the toy. */
  const setLaneLevel = useCallback(
    (which: Lane, v: number) => {
      const targets = controlsRef.current.filter(c => (which === 'turn' ? c.bidi : !c.bidi));
      if (!targets.length) return;
      setLevels(prev => {
        const next = { ...prev };
        for (const c of targets) next[c.id] = v;
        return next;
      });
      for (const c of targets) publish(c.id, v);
    },
    [publish],
  );

  /** Set a slider (and the ones linked to it) to a level, here and on the toy. */
  const setOne = useCallback(
    (c: Control, v: number) => {
      const run = linkedRun(controlsRef.current, linksRef.current, c);
      setLevels(prev => {
        const next = { ...prev };
        for (const t of run) next[t.id] = v;
        return next;
      });
      for (const t of run) publish(t.id, v);
    },
    [publish],
  );

  // One clock for both lanes, so a vibration pattern and a twirl pattern
  // can play together.
  const ensureTicker = useCallback(() => {
    if (ticker.current) return;
    ticker.current = window.setInterval(() => {
      let any = false;
      (['vibe', 'turn'] as Lane[]).forEach(l => {
        const loop = loops.current[l];
        if (!loop) return;
        any = true;
        setLaneLevel(l, loop.values[loop.i % loop.values.length]);
        loop.i++;
      });
      if (!any && ticker.current) {
        clearInterval(ticker.current);
        ticker.current = null;
      }
    }, FRAME_MS);
  }, [setLaneLevel]);

  const playOn = (which: Lane, id: string, values: number[]) => {
    if (!values.length) return;
    loops.current[which] = { values, i: 0 };
    setPlaying(p => ({ ...p, [which]: id }));
    ensureTicker();
  };
  const stopLane = (which: Lane) => {
    loops.current[which] = null;
    setPlaying(p => ({ ...p, [which]: null }));
    setLaneLevel(which, which === 'turn' ? 0.5 : 0);
  };
  const stopAll = useCallback(() => {
    loops.current = { vibe: null, turn: null };
    setPlaying({ vibe: null, turn: null });
    for (const c of controlsRef.current) {
      publish(c.id, restOf(c));
    }
    setLevels({});
  }, [publish]);

  // The room itself. Shared links know its key from the start; code mode
  // only learns it when the sharer lets them in.
  useEffect(() => {
    if (!topic) return;
    const ch = supabase.channel(topic);
    channelRef.current = ch;
    /** The sharer's own moves: the newest number seen per control. */
    const appSeq = new Map<string, number>();

    ch.on('broadcast', { event: 'control' }, ({ payload }) => {
      // The app ends the session through a control message too.
      if (payload?.event === 'session_end') {
        setSessionEnded(true);
        stopAll();
        return;
      }
      // The app asks who's here (it opened or came back): say so.
      if (payload?.event === 'ping') {
        announce('join');
        return;
      }
      if (payload?.event === 'away') {
        setAway(true);
        return;
      }
      if (payload?.event === 'back') {
        setAway(false);
        return;
      }
      // The sharer switched a motor on or off on their phone: show it.
      if (payload?.event === 'power' && typeof payload.joystickId === 'string' && typeof payload.on === 'boolean') {
        const next = { ...offRef.current, [payload.joystickId]: !payload.on };
        offRef.current = next;
        setOff(next);
        return;
      }
      // What the toy has: a slider per control.
      if (payload?.event === 'layout' && Array.isArray(payload.controls)) {
        const next = (payload.controls as unknown[])
          .filter((c): c is Control => !!c && typeof (c as Control).id === 'string')
          .slice(0, 6)
          .map(c => ({
            id: c.id,
            bidi: !!c.bidi,
            ...(!c.bidi && (c.kind === 'thrust' || c.kind === 'thrust-side') ? { kind: c.kind } : {}),
          }));
        if (next.length) setControls(next);
        setAway(false);
        return;
      }
      // The sharer moved a slider on their phone: show it.
      if (typeof payload?.joystickId === 'string' && typeof payload?.amplitude === 'number') {
        // Out of order: an older level than one already shown.
        if (Number.isInteger(payload.seq)) {
          const last = appSeq.get(payload.joystickId) ?? -1;
          if (payload.seq <= last) return;
          appSeq.set(payload.joystickId, payload.seq);
        }
        const v = Math.max(0, Math.min(1, payload.amplitude));
        setLevels(prev => ({ ...prev, [payload.joystickId]: v }));
      }
    });

    ch.on('broadcast', { event: 'session_end' }, () => {
      setSessionEnded(true);
      stopAll();
    });

    // Presence for the app: 'join' once connected (the app shows "Connected",
    // opens its controller and answers with the toy's layout), 'leave' when
    // this page closes or is hidden. Sent as control messages with an
    // `event`, which app versions that don't know them ignore.
    const announce = (event: 'join' | 'leave') => {
      ch.send({ type: 'broadcast', event: 'control', payload: { event, vid } });
    };
    const onHide = () => announce('leave');
    const onVisible = () => {
      if (document.visibilityState === 'visible') announce('join');
      else announce('leave');
    };

    ch.subscribe(status => {
      setConnected(status === 'SUBSCRIBED');
      if (status === 'SUBSCRIBED') announce('join');
    });
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisible);
      announce('leave');
      ch.unsubscribe();
      channelRef.current = null;
    };
  }, [topic, stopAll, vid]);

  // Code mode: wait in the lobby (room:<RoomId>:<LobbyKey>), knocking every
  // 3 s, until the sharer answers. 'admit' carries the room's key sealed to
  // this visitor (see openAdmit); it lives only in memory. Nothing but
  // knock/leave is ever sent here.
  useEffect(() => {
    if (!viaCode || !lobbyKey || mainKey || admission === 'denied') return;
    const lobby = supabase.channel(topicFor(roomId, lobbyKey));
    let knockTimer: number | null = null;
    let answered = false;
    let me: Visitor | null = null;
    let subscribed = false;
    let closed = false;
    // One identity per visit (kept across this effect re-running).
    visitorRef.current ??= makeVisitor();
    visitorRef.current.then(v => {
      if (closed) return;
      me = v;
      if (subscribed && !answered) startKnocking();
    });
    const say = (payload: Record<string, unknown>) => {
      lobby.send({ type: 'broadcast', event: 'control', payload });
    };
    const knock = () => {
      if (me) say({ event: 'knock', via: 'code', id: me.id, pub: me.pub });
    };
    const stopKnocking = () => {
      if (knockTimer !== null) clearInterval(knockTimer);
      knockTimer = null;
    };
    const startKnocking = () => {
      knock();
      if (knockTimer === null) knockTimer = window.setInterval(knock, 3000);
    };

    lobby.on('broadcast', { event: 'control' }, ({ payload }) => {
      if (answered) return;
      if (payload?.event === 'ping') {
        knock();
        return;
      }
      if (payload?.event === 'session_end') {
        answered = true;
        stopKnocking();
        setSessionEnded(true);
        return;
      }
      // Only answers addressed to this visitor count; the key is sealed to us.
      if (!me || payload?.to !== me.id) return;
      if (payload.event === 'admit') {
        openAdmit(me, payload).then(key => {
          if (!key || answered || closed) return;
          answered = true;
          stopKnocking();
          admittedRef.current = true;
          setMainKey(key);
          setAdmission('open');
        });
        return;
      }
      if (payload.event === 'deny') {
        answered = true;
        stopKnocking();
        setAdmission('denied');
        return;
      }
    });

    const leave = () => say(me ? { event: 'leave', id: me.id } : { event: 'leave' });
    const onHide = () => leave();
    const onVisible = () => {
      if (answered) return;
      if (document.visibilityState === 'visible') knock();
      else leave();
    };

    lobby.subscribe(status => {
      setConnected(status === 'SUBSCRIBED');
      subscribed = status === 'SUBSCRIBED';
      if (subscribed && !answered && me) startKnocking();
    });
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopKnocking();
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisible);
      closed = true;
      if (!answered) leave();
      lobby.unsubscribe();
    };
  }, [viaCode, lobbyKey, roomId, mainKey, admission]);

  useEffect(
    () => () => {
      if (ticker.current) clearInterval(ticker.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    },
    [],
  );

  // ── Draw ──
  const pointFrom = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  };
  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    capture(e.currentTarget, e.pointerId);
    if (playing[lane]) {
      loops.current[lane] = null;
      setPlaying(p => ({ ...p, [lane]: null }));
    }
    const p = pointFrom(e);
    setStrokes(s => ({ ...s, [lane]: [p] }));
    setIsDrawing(true);
    setLaneLevel(lane, 1 - p.y);
  };
  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const p = pointFrom(e);
    setStrokes(s => ({ ...s, [lane]: [...s[lane], p] }));
    // Feel it while drawing, like the app's Studio.
    setLaneLevel(lane, 1 - p.y);
  };
  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setLaneLevel(lane, lane === 'turn' ? 0.5 : 0);
  };
  const drawLevels = useMemo(() => ({ vibe: strokeLevels(strokes.vibe), turn: strokeLevels(strokes.turn) }), [strokes]);
  const drawingPlaying = playing.vibe === 'draw' || playing.turn === 'draw';
  const hasDrawing = drawLevels.vibe.length > 0 || (hasTurn && drawLevels.turn.length > 0);
  const playDrawing = () => {
    if (drawLevels.vibe.length) playOn('vibe', 'draw', drawLevels.vibe);
    if (hasTurn && drawLevels.turn.length) playOn('turn', 'draw', drawLevels.turn);
  };
  const stopDrawingPlayback = () => {
    if (playing.vibe === 'draw') stopLane('vibe');
    if (playing.turn === 'draw') stopLane('turn');
  };
  const clearLane = () => {
    if (playing[lane] === 'draw') stopLane(lane);
    setStrokes(s => ({ ...s, [lane]: [] }));
  };

  // Paint both lanes (the one being drawn on in front), sharp at any size.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const paint = () => {
      const dpr = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);
      const order: Lane[] = lane === 'vibe' ? ['turn', 'vibe'] : ['vibe', 'turn'];
      for (const l of order) {
        const pts = strokes[l];
        if (pts.length < 2 || (l === 'turn' && !hasTurn)) continue;
        const front = l === lane;
        ctx.globalAlpha = front ? 1 : 0.35;
        const grad = ctx.createLinearGradient(0, 0, width, 0);
        grad.addColorStop(0, l === 'turn' ? '#7B2FFF' : '#FF2FBF');
        grad.addColorStop(1, l === 'turn' ? '#B57BFF' : '#9B3FE8');
        ctx.strokeStyle = grad;
        ctx.lineWidth = front ? 4 : 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.shadowColor = front ? 'rgba(255,47,191,0.6)' : 'transparent';
        ctx.shadowBlur = front ? 12 : 0;
        ctx.beginPath();
        pts.forEach((p, i) => (i ? ctx.lineTo(p.x * width, p.y * height) : ctx.moveTo(p.x * width, p.y * height)));
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };
    paint();
    window.addEventListener('resize', paint);
    return () => window.removeEventListener('resize', paint);
  }, [strokes, lane, mode, hasTurn, admission]);

  // The live line: what their toy is doing, scrolling right to left.
  useEffect(() => {
    const canvas = waveCanvasRef.current;
    if (!canvas) return;
    const animate = () => {
      const dpr = window.devicePixelRatio || 1;
      const { width, height } = canvas.getBoundingClientRect();
      if (canvas.width !== Math.round(width * dpr)) {
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const pts = wavePointsRef.current;
      const last = pts[pts.length - 1];
      pts.shift();
      pts.push(last + (vibeLevelRef.current - last) * 0.35);
      ctx.clearRect(0, 0, width, height);
      const pad = 10;
      const x = (i: number) => (i / (pts.length - 1)) * (width - pad * 2) + pad;
      const y = (v: number) => height - pad - v * (height - pad * 2);
      const line = new Path2D();
      pts.forEach((v, i) => (i ? line.lineTo(x(i), y(v)) : line.moveTo(x(i), y(v))));
      const area = new Path2D(line);
      area.lineTo(x(pts.length - 1), height);
      area.lineTo(x(0), height);
      area.closePath();
      const fill = ctx.createLinearGradient(0, 0, 0, height);
      fill.addColorStop(0, 'rgba(255,47,191,0.45)');
      fill.addColorStop(1, 'rgba(155,63,232,0)');
      ctx.fillStyle = fill;
      ctx.fill(area);
      const stroke = ctx.createLinearGradient(0, 0, width, 0);
      stroke.addColorStop(0, '#9B3FE8');
      stroke.addColorStop(1, '#FF2FBF');
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.shadowColor = 'rgba(255,47,191,0.7)';
      ctx.shadowBlur = 10;
      ctx.stroke(line);
      ctx.shadowBlur = 0;
      const nx = x(pts.length - 1);
      const ny = y(pts[pts.length - 1]);
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(nx, ny, 5, 0, Math.PI * 2);
      ctx.fill();
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
    // Re-run when the controls first appear (code mode waits before showing them).
  }, [admission]);

  // ── Sliders (vertical: bottom is off / full back, top is full / full forward) ──
  const [dragging, setDragging] = useState<string | null>(null);
  /** The level under the finger; `inset` is where the thumb's centre stops at each end. */
  const valueAt = (el: HTMLDivElement, clientY: number, inset: number) => {
    const rect = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (rect.bottom - inset - clientY) / (rect.height - inset * 2)));
  };
  const moveSlider = (c: Control, el: HTMLDivElement, clientY: number) => {
    const raw = valueAt(el, clientY, c.bidi ? 42 : 56);
    // Snap to stopped near the middle.
    const v = c.bidi && Math.abs(raw - 0.5) < 0.04 ? 0.5 : raw;
    // A finger on a slider takes over from a pattern on that lane.
    const l: Lane = c.bidi ? 'turn' : 'vibe';
    if (loops.current[l]) {
      loops.current[l] = null;
      setPlaying(p => ({ ...p, [l]: null }));
    }
    lastTouchedRef.current = c.id;
    setOne(c, v);
  };
  /** The chain between slider `i` and the next: linking snaps the pair's run to the one touched last (or the highest). */
  const toggleLink = (i: number) => {
    const a = controls[i];
    const b = controls[i + 1];
    const key = linkKey(a, b);
    const next = { ...links, [key]: !links[key] };
    setLinks(next);
    linksRef.current = next;
    if (!next[key]) return;
    const run = linkedRun(controls, next, a);
    const leader = run.find(t => t.id === lastTouchedRef.current) ?? run.reduce((best, t) => (levelOf(t) > levelOf(best) ? t : best), run[0]);
    setOne(leader, levelOf(leader));
  };
  /** A slider's power button (with its linked sliders): off sends its rest level and keeps the slider where it is; on sends that level again. */
  const togglePower = (c: Control) => {
    const run = linkable ? linkedRun(controls, links, c) : [c];
    const turnOff = !off[c.id];
    const next = { ...off };
    for (const t of run) next[t.id] = turnOff;
    offRef.current = next;
    setOff(next);
    for (const t of run) publish(t.id, levelOf(t));
  };
  const sliderHandlers = (c: Control) => ({
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      capture(e.currentTarget, e.pointerId);
      setDragging(c.id);
      moveSlider(c, e.currentTarget, e.clientY);
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragging !== c.id) return;
      e.preventDefault();
      moveSlider(c, e.currentTarget, e.clientY);
    },
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => {
      setDragging(null);
      release(e.currentTarget, e.pointerId);
    },
    onPointerCancel: () => setDragging(null),
  });

  const turnText = (v: number) => {
    const percent = Math.round((v - 0.5) * 200);
    if (percent === 0) return 'Stopped';
    // Below the centre turns clockwise, as on the app's Controller.
    return `${Math.abs(percent)}% ${percent < 0 ? '↻' : '↺'}`;
  };
  const sliderName = (c: Control) => {
    if (c.kind) return 'Thrust';
    const same = c.bidi ? turnControls : vibeControls.filter(v => !v.kind);
    const base = c.bidi ? turnName : 'Vibration';
    return same.length > 1 ? `${base} ${same.indexOf(c) + 1}` : base;
  };

  const switchMode = (m: Mode) => {
    stopAll();
    setMode(m);
  };

  // Back (or a reload) onto a session that already ended: say so, never rejoin.
  if (endedBefore) {
    return (
      <div className="rc">
        <Header connected={false} hideStatus />
        <main className="content">
          <section className="card empty">
            <Bunny size={96} glow={0} />
            <h2>Session ended</h2>
            <p>They closed the session. To play again, ask them for a new code.</p>
            <Link className="btn primary wide" to="/remote">
              Enter a code
            </Link>
          </section>
          <GetFluffer title="Get Fluffer" />
        </main>
      </div>
    );
  }

  if (lostCodeVisit) {
    return (
      <div className="rc">
        <Header connected={false} hideStatus />
        <main className="content">
          <section className="card empty">
            <Bunny size={96} />
            <h2>This page was reloaded</h2>
            <p>Codes work once, so ask them for a new one and enter it again.</p>
            <Link className="btn primary wide" to="/remote">
              Enter a code
            </Link>
          </section>
          <GetFluffer title="Get Fluffer" />
        </main>
      </div>
    );
  }

  // Nothing in the address at all (someone typed the site, or was told "go to
  // share.fluffer.app"): they have a code, not a link. Straight to code entry.
  if (!validLink && !partial) return <Navigate to="/remote" replace />;

  if (!validLink) {
    return (
      <div className="rc">
        <Header connected={false} hideStatus />
        <main className="content">
          <section className="card empty">
            <Bunny size={96} />
            <h2>This link isn’t complete</h2>
            <p>Ask the person who shared it to send it again, or open it from the message they sent. Got a code instead?</p>
            <Link className="btn primary wide" to="/remote">
              Enter a code
            </Link>
          </section>
          <GetFluffer title="Get Fluffer" />
        </main>
      </div>
    );
  }

  // Code mode, before the sharer lets them in: no controls at all.
  if (admission !== 'open') {
    return (
      <div className="rc">
        <Header connected={connected} />
        <main className="content">
          <div className="stage waiting-stage">
            {admission === 'knocking' ? (
              <section className="card waiting" role="status" aria-live="polite">
                <div className="waiting-bunny">
                  <Bunny size={120} />
                </div>
                <h2>Waiting for them to let you in…</h2>
                <p>They’ll see a request on their phone. The controls appear here as soon as they say yes.</p>
                <span className="waiting-dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </span>
                {slow ? (
                  <Link className="link-btn waiting-new" to="/remote">
                    Taking too long? Enter a new code
                  </Link>
                ) : null}
              </section>
            ) : (
              <section className="card waiting" role="status" aria-live="polite">
                <Bunny size={96} glow={0} />
                <h2>They didn’t let you in.</h2>
                <p>If that was a mistake, ask them for a new code and try again.</p>
                <Link className="btn primary wide" to="/remote">
                  Enter a code
                </Link>
              </section>
            )}
          </div>

          <GetFluffer />

          <footer className="foot">
            Fluffer by Motorbunny · <a href="https://www.motorbunny.com" target="_blank" rel="noopener noreferrer">motorbunny.com</a>
          </footer>
        </main>

        {sessionEnded && (
          <div className="overlay">
            <div className="modal">
              <Bunny size={88} />
              <h2>Session ended</h2>
              <p>They closed the session. To play again, ask them for a new code.</p>
              <Link className="btn primary wide" to="/remote" replace>
                Enter a code
              </Link>
              <GetFluffer title="Keep playing in Fluffer" />
            </div>
          </div>
        )}
      </div>
    );
  }

  const pct = Math.round(vibeLevel * 100);

  const patternColumn = (which: Lane, list: PagePattern[], title: string) => (
    <div className="pattern-col">
      <div className="col-head">
        <span className="eyebrow">{title}</span>
        {playing[which] && playing[which] !== 'draw' && (
          <button className="link-btn" onClick={() => stopLane(which)}>
            Stop
          </button>
        )}
      </div>
      <div className="pattern-list">
        {list.map(p => (
          <button key={p.id} className={`pattern ${playing[which] === p.id ? 'on' : ''} ${which === 'turn' ? 'turn' : ''}`} onClick={() => (playing[which] === p.id ? stopLane(which) : playOn(which, p.id, p.values))}>
            <Sparkline values={p.values} bidi={which === 'turn'} />
            <span className="pattern-name">{p.name}</span>
            <span className="pattern-sub">{playing[which] === p.id ? 'Playing · tap to stop' : p.feel}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="rc">
      <Header connected={connected} />

      <main className="content">
        {/* Everything needed to play fits one screen; the rest is below. */}
        <div className="stage">
        {away && (
          <section className="away" role="status">
            <span className="away-dot" />
            <div>
              <strong>Their phone went to sleep</strong>
              <p>It picks up again as soon as they open Fluffer.</p>
            </div>
          </section>
        )}

        <section className="card live">
          <div className="live-top">
            <span className="live-title">
              <span className="eyebrow">You’re in control</span>
              <span className="live-sub">They feel what you do, live</span>
            </span>
            <span className="live-nums">
              {hasTurn && <span className="live-turn">{turnText(turnLevel)}</span>}
              <span className="live-pct">
                {pct}
                <small>%</small>
              </span>
            </span>
          </div>
          <canvas ref={waveCanvasRef} className="wave" />
        </section>

        <nav className="tabs" role="tablist">
          {(
            [
              ['control', 'Slide', 'sliders'],
              ['draw', 'Draw', 'pencil'],
              ['patterns', 'Patterns', 'waves'],
            ] as const
          ).map(([m, label, icon]) => (
            <button key={m} role="tab" aria-selected={mode === m} className={`tab ${mode === m ? 'on' : ''}`} onClick={() => switchMode(m)}>
              <Icon name={icon} size={16} />
              {label}
            </button>
          ))}
        </nav>

        {mode === 'control' && (
          <section className={`card sliders powered n${Math.min(controls.length, 4)} ${linkable ? 'linkable' : ''}`}>
            {controls.map(c => {
              const v = levelOf(c);
              const tied = linkable && linkedRun(controls, links, c).length > 1 ? 'linked' : '';
              return (
                <div className={`slider ${off[c.id] ? 'off' : ''}`} key={c.id}>
                  <div className="slider-label">
                    {c.kind ? <ThrustIcon sideways={c.kind === 'thrust-side'} /> : null}
                    {sliderName(c)}
                  </div>
                  {c.bidi ? (
                    <div className={`track rot ${dragging === c.id ? 'active' : ''}`} {...sliderHandlers(c)} role="slider" aria-label={sliderName(c)} aria-valuemin={-100} aria-valuemax={100} aria-valuenow={Math.round((v - 0.5) * 200)}>
                      {/* Clipped to the track's rounded ends, so a full twirl doesn't square them off. */}
                      <div className="rot-clip">
                        <div className="fill rot-fill" style={v >= 0.5 ? { bottom: '50%', height: `${(v - 0.5) * 100}%` } : { bottom: `${v * 100}%`, height: `${(0.5 - v) * 100}%` }} />
                      </div>
                      <div className="center-line" />
                      <div className="thumb knob" style={{ bottom: `calc(${v} * (100% - 84px) + 14px)` }}>
                        <Icon name="rotate" size={22} />
                      </div>
                    </div>
                  ) : (
                    <div className={`track ${tied} ${dragging === c.id ? 'active' : ''}`} {...sliderHandlers(c)} role="slider" aria-label={sliderName(c)} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(v * 100)}>
                      <div className="fill" style={{ height: v > 0 ? `calc(${v} * (100% - 112px) + 112px)` : 0 }} />
                      <div className="ticks" />
                      <div className="thumb" style={{ bottom: `calc(${v} * (100% - 112px) + 12px)` }}>
                        <Bunny size={88} glow={v} />
                      </div>
                    </div>
                  )}
                  <div className="slider-value">{off[c.id] ? 'Off' : c.bidi ? turnText(v) : `${Math.round(v * 100)}%`}</div>
                </div>
              );
            })}
            {/* A power button under each slider; between linkable ones, the chain. */}
            <div className="links" style={{ '--n': Math.min(controls.length, 4) } as React.CSSProperties}>
              {controls.slice(0, 4).map((c, i) => (
                <button
                  key={`power-${c.id}`}
                  className={`power ${off[c.id] ? '' : 'on'}`}
                  style={{ '--i': i } as React.CSSProperties}
                  onClick={() => togglePower(c)}
                  aria-label={`${sliderName(c)} ${off[c.id] ? 'off' : 'on'}`}
                  aria-pressed={!off[c.id]}
                >
                  <Icon name="power" size={18} />
                </button>
              ))}
              {linkable &&
                controls.slice(0, 4).map((c, i) => {
                  const d = controls[i + 1];
                  if (!d || i >= 3 || c.bidi || d.bidi) return null;
                  const on = !!links[linkKey(c, d)];
                  return (
                    <React.Fragment key={linkKey(c, d)}>
                      {on && <div className="link-line" style={{ '--i': i } as React.CSSProperties} />}
                      <button
                        className={`link ${on ? 'on' : ''}`}
                        style={{ '--i': i } as React.CSSProperties}
                        onClick={() => toggleLink(i)}
                        aria-label={on ? 'Unlink motors' : 'Link motors'}
                        aria-pressed={on}
                      >
                        <Icon name={on ? 'link' : 'unlink'} size={16} />
                      </button>
                    </React.Fragment>
                  );
                })}
            </div>
          </section>
        )}

        {mode === 'draw' && (
          <section className="card draw">
            <div className="draw-head">
              {hasTurn ? (
                <div className="lanes" role="tablist">
                  {(['vibe', 'turn'] as Lane[]).map(l => (
                    <button key={l} role="tab" aria-selected={lane === l} className={`lane ${lane === l ? 'on' : ''} ${l}`} onClick={() => !isDrawing && setLaneState(l)}>
                      {l === 'vibe' ? 'Vibration' : turnName}
                      {strokes[l].length > 1 ? ' •' : ''}
                    </button>
                  ))}
                </div>
              ) : (
                <span className="eyebrow">Draw a pattern</span>
              )}
              <button className="icon-btn" onClick={clearLane} aria-label="Clear this drawing" disabled={!strokes[lane].length}>
                <Icon name="eraser" size={18} />
              </button>
            </div>
            <div className={`draw-box ${lane === 'turn' ? 'turn' : ''}`}>
              {lane === 'turn' && <div className="draw-mid" />}
              <canvas ref={canvasRef} className="draw-canvas" onPointerDown={startDrawing} onPointerMove={draw} onPointerUp={stopDrawing} onPointerCancel={stopDrawing} />
              {!strokes[lane].length && (
                <div className="draw-empty">
                  {lane === 'turn'
                    ? 'Draw left to right. Below the line turns clockwise, above turns counter-clockwise. They feel it as you draw.'
                    : 'Draw left to right. Higher is stronger, and they feel it as you draw.'}
                </div>
              )}
            </div>
            {hasTurn && <p className="draw-note">Draw both, then play: they run together.</p>}
            <button className={`btn primary wide ${!hasDrawing && !drawingPlaying ? 'dim' : ''}`} disabled={!hasDrawing && !drawingPlaying} onClick={drawingPlaying ? stopDrawingPlayback : playDrawing}>
              <Icon name={drawingPlaying ? 'square' : 'play'} size={16} />
              {drawingPlaying ? 'Stop' : 'Play on repeat'}
            </button>
          </section>
        )}

        {mode === 'patterns' && (
          <section className={`patterns ${hasTurn ? 'two' : ''}`}>
            {patternColumn('vibe', VIBE_PATTERNS, 'Vibration')}
            {hasTurn && patternColumn('turn', TWIRL_PATTERNS, turnName)}
          </section>
        )}

        <button className="btn stop" onClick={stopAll}>
          <Icon name="square" size={14} />
          Stop
        </button>
        </div>

        <GetFluffer />

        <footer className="foot">
          Fluffer by Motorbunny · <a href="https://www.motorbunny.com" target="_blank" rel="noopener noreferrer">motorbunny.com</a>
        </footer>
      </main>

      {sessionEnded && (
        <div className="overlay">
          <div className="modal">
            <Bunny size={88} />
            <h2>Session ended</h2>
            <p>They closed the session. To play again, ask them for a new code.</p>
            <Link className="btn primary wide" to="/remote" replace>
              Enter a code
            </Link>
            <GetFluffer title="Keep playing in Fluffer" />
          </div>
        </div>
      )}
    </div>
  );
};

export function Header({ connected, hideStatus }: { connected: boolean; hideStatus?: boolean }) {
  return (
    <header className="bar">
      <img src="/fluffer-logo.png" alt="Fluffer by Motorbunny" className="logo" />
      {!hideStatus && (
        <span className={`status ${connected ? 'on' : ''}`}>
          <i />
          {connected ? 'Live' : 'Connecting…'}
        </span>
      )}
    </header>
  );
}

export default RemoteController;
